import React from 'react';
import { useRownd } from '@rownd/react';
import { Typography, Container, Grid, Card, CardContent, Box, Button, LinearProgress, Divider } from '@mui/material';
import { Group, Code, InsertChart, Settings, WebAsset } from '@mui/icons-material';

const DashboardPage: React.FC = () => {
  const { user } = useRownd();

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-100 to-gray-200 pb-12">
      <Box className="py-12 bg-gradient-to-r from-purple-700 to-blue-500 text-white mb-8">
        <Container maxWidth="lg">
          <Typography variant="h3" component="h1" className="font-bold mb-2">
            Welcome back, {user.data.first_name || 'Viber'}!
          </Typography>
          <Typography variant="h6">
            Your VIBE Coding Dashboard
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* Stats Overview */}
        <Grid container spacing={4} className="mb-8">
          <Grid item xs={12} sm={6} md={3}>
            <Card className="h-full shadow">
              <CardContent className="flex flex-col items-center text-center">
                <WebAsset className="text-purple-600 text-5xl mb-2" />
                <Typography variant="h5" component="h2" className="font-bold text-gray-800">
                  2
                </Typography>
                <Typography variant="body2" className="text-gray-500">
                  Active Projects
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card className="h-full shadow">
              <CardContent className="flex flex-col items-center text-center">
                <Code className="text-purple-600 text-5xl mb-2" />
                <Typography variant="h5" component="h2" className="font-bold text-gray-800">
                  42
                </Typography>
                <Typography variant="body2" className="text-gray-500">
                  VIBE Sessions
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card className="h-full shadow">
              <CardContent className="flex flex-col items-center text-center">
                <InsertChart className="text-purple-600 text-5xl mb-2" />
                <Typography variant="h5" component="h2" className="font-bold text-gray-800">
                  87%
                </Typography>
                <Typography variant="body2" className="text-gray-500">
                  Code Efficiency
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card className="h-full shadow">
              <CardContent className="flex flex-col items-center text-center">
                <Group className="text-purple-600 text-5xl mb-2" />
                <Typography variant="h5" component="h2" className="font-bold text-gray-800">
                  5
                </Typography>
                <Typography variant="body2" className="text-gray-500">
                  Team Members
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Main Content */}
        <Grid container spacing={4}>
          {/* Left Column */}
          <Grid item xs={12} md={8}>
            <Card className="shadow mb-6">
              <CardContent>
                <Box className="flex justify-between items-center mb-4">
                  <Typography variant="h5" component="h2" className="font-bold text-gray-800">
                    Your VIBE Projects
                  </Typography>
                  <Button variant="contained" color="primary" className="bg-purple-600 hover:bg-purple-700">
                    New Project
                  </Button>
                </Box>
                <Divider className="mb-4" />

                {/* Project 1 */}
                <Box className="mb-6 p-4 border border-gray-200 rounded hover:bg-gray-50 transition-colors">
                  <Box className="flex justify-between items-center mb-2">
                    <Typography variant="h6" className="font-bold text-gray-800">
                      E-commerce Dashboard
                    </Typography>
                    <Typography variant="body2" className="text-gray-500">
                      Updated 2 days ago
                    </Typography>
                  </Box>
                  <Typography variant="body2" className="text-gray-600 mb-3">
                    React frontend with Rownd authentication and Stripe integration.
                  </Typography>
                  <Box className="mb-2">
                    <Box className="flex justify-between mb-1">
                      <Typography variant="body2" className="text-gray-700">
                        Progress
                      </Typography>
                      <Typography variant="body2" className="text-gray-700">
                        75%
                      </Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={75} className="mb-3" />
                  </Box>
                  <Box className="flex space-x-2">
                    <Button size="small" variant="outlined" color="primary">
                      Open Project
                    </Button>
                    <Button size="small" variant="outlined" color="secondary">
                      View Code
                    </Button>
                  </Box>
                </Box>

                {/* Project 2 */}
                <Box className="p-4 border border-gray-200 rounded hover:bg-gray-50 transition-colors">
                  <Box className="flex justify-between items-center mb-2">
                    <Typography variant="h6" className="font-bold text-gray-800">
                      Personal Blog
                    </Typography>
                    <Typography variant="body2" className="text-gray-500">
                      Updated 5 hours ago
                    </Typography>
                  </Box>
                  <Typography variant="body2" className="text-gray-600 mb-3">
                    NextJS blog with Rownd user comments and markdown support.
                  </Typography>
                  <Box className="mb-2">
                    <Box className="flex justify-between mb-1">
                      <Typography variant="body2" className="text-gray-700">
                        Progress
                      </Typography>
                      <Typography variant="body2" className="text-gray-700">
                        45%
                      </Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={45} className="mb-3" />
                  </Box>
                  <Box className="flex space-x-2">
                    <Button size="small" variant="outlined" color="primary">
                      Open Project
                    </Button>
                    <Button size="small" variant="outlined" color="secondary">
                      View Code
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Card className="shadow">
              <CardContent>
                <Typography variant="h5" component="h2" className="font-bold text-gray-800 mb-4">
                  Recent Activity
                </Typography>
                <Divider className="mb-4" />
                <Box className="space-y-4">
                  <Box className="flex items-start">
                    <Box className="bg-purple-100 rounded-full p-2 mr-3">
                      <Code className="text-purple-600" />
                    </Box>
                    <Box>
                      <Typography variant="body1" className="font-medium text-gray-800">
                        Generated React component with VIBE Coding
                      </Typography>
                      <Typography variant="body2" className="text-gray-500">
                        2 hours ago • E-commerce Dashboard
                      </Typography>
                    </Box>
                  </Box>
                  <Box className="flex items-start">
                    <Box className="bg-blue-100 rounded-full p-2 mr-3">
                      <WebAsset className="text-blue-600" />
                    </Box>
                    <Box>
                      <Typography variant="body1" className="font-medium text-gray-800">
                        Deployed Personal Blog to production
                      </Typography>
                      <Typography variant="body2" className="text-gray-500">
                        Yesterday • Personal Blog
                      </Typography>
                    </Box>
                  </Box>
                  <Box className="flex items-start">
                    <Box className="bg-green-100 rounded-full p-2 mr-3">
                      <Settings className="text-green-600" />
                    </Box>
                    <Box>
                      <Typography variant="body1" className="font-medium text-gray-800">
                        Updated Rownd authentication settings
                      </Typography>
                      <Typography variant="body2" className="text-gray-500">
                        3 days ago • Account Settings
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Column */}
          <Grid item xs={12} md={4}>
            <Card className="shadow mb-6">
              <CardContent>
                <Typography variant="h5" component="h2" className="font-bold text-gray-800 mb-4">
                  Subscription
                </Typography>
                <Divider className="mb-4" />
                <Box className="mb-4 p-4 bg-purple-50 rounded-lg border border-purple-100">
                  <Typography variant="h6" className="font-bold text-purple-800 mb-1">
                    Basic Plan
                  </Typography>
                  <Typography variant="body2" className="text-gray-600 mb-3">
                    $1/month while vibing
                  </Typography>
                  <Box className="flex items-center justify-between">
                    <Typography variant="body2" className="text-gray-600">
                      Next billing: April 24, 2025
                    </Typography>
                    <Button size="small" variant="outlined" color="primary">
                      Upgrade
                    </Button>
                  </Box>
                </Box>
                <Box className="mb-2">
                  <Typography variant="body2" className="font-medium text-gray-700 mb-1">
                    Usage this month
                  </Typography>
                  <Box className="flex justify-between mb-1">
                    <Typography variant="body2" className="text-gray-600">
                      VIBE Sessions
                    </Typography>
                    <Typography variant="body2" className="text-gray-600">
                      12/Unlimited
                    </Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={30} className="mb-2" />
                  <Box className="flex justify-between mb-1">
                    <Typography variant="body2" className="text-gray-600">
                      Projects
                    </Typography>
                    <Typography variant="body2" className="text-gray-600">
                      2/5
                    </Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={40} className="mb-2" />
                </Box>
              </CardContent>
            </Card>

            <Card className="shadow mb-6">
              <CardContent>
                <Typography variant="h5" component="h2" className="font-bold text-gray-800 mb-4">
                  Team Members
                </Typography>
                <Divider className="mb-4" />
                <Box className="space-y-3">
                  <Box className="flex items-center justify-between">
                    <Box className="flex items-center">
                      <div className="w-8 h-8 rounded-full bg-purple-200 flex items-center justify-center mr-2">
                        <Typography variant="body2" className="font-bold text-purple-800">
                          {user.data.first_name?.[0] || 'U'}
                        </Typography>
                      </div>
                      <Typography variant="body1" className="font-medium text-gray-800">
                        {user.data.first_name || 'You'} (Owner)
                      </Typography>
                    </Box>
                  </Box>
                  <Box className="flex items-center justify-between">
                    <Box className="flex items-center">
                      <div className="w-8 h-8 rounded-full bg-blue-200 flex items-center justify-center mr-2">
                        <Typography variant="body2" className="font-bold text-blue-800">
                          J
                        </Typography>
                      </div>
                      <Typography variant="body1" className="font-medium text-gray-800">
                        Jane Smith
                      </Typography>
                    </Box>
                  </Box>
                  <Box className="flex items-center justify-between">
                    <Box className="flex items-center">
                      <div className="w-8 h-8 rounded-full bg-green-200 flex items-center justify-center mr-2">
                        <Typography variant="body2" className="font-bold text-green-800">
                          M
                        </Typography>
                      </div>
                      <Typography variant="body1" className="font-medium text-gray-800">
                        Mike Johnson
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Button 
                  variant="outlined" 
                  color="primary" 
                  fullWidth 
                  className="mt-4"
                >
                  Invite Team Member
                </Button>
              </CardContent>
            </Card>

            <Card className="shadow">
              <CardContent>
                <Typography variant="h5" component="h2" className="font-bold text-gray-800 mb-4">
                  Resources
                </Typography>
                <Divider className="mb-4" />
                <Box className="space-y-3">
                  <Button 
                    variant="text" 
                    color="primary" 
                    fullWidth 
                    className="justify-start"
                    startIcon={<Code />}
                  >
                    VIBE Coding Templates
                  </Button>
                  <Button 
                    variant="text" 
                    color="primary" 
                    fullWidth 
                    className="justify-start"
                    startIcon={<WebAsset />}
                  >
                    Deployment Guide
                  </Button>
                  <Button 
                    variant="text" 
                    color="primary" 
                    fullWidth 
                    className="justify-start"
                    startIcon={<Settings />}
                  >
                    Rownd Integration Docs
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </div>
  );
};

export default DashboardPage; 